import { useQuery, type TypedDocumentNode } from '@apollo/client';
import type { ResultOf, VariablesOf } from '@newfront-insurance/graphql-frontend';
import { useMemo, useState } from 'react';
import { useDebounce } from 'react-use';

import type { SettingsEmployee } from './employee-utils';
import { SEARCH_NOVA_SHAREABLE_USERS } from '../../../../queries/chats';

export const PEOPLE_RESULT_LIMIT = 8;

export function useSettingsEmployeeSearch(query: string, enabled = true) {
  return useEmployeeSearch(query, { document: SEARCH_NOVA_SHAREABLE_USERS, enabled });
}

export function useEmployeeSearch(
  query: string,
  {
    document,
    enabled = true,
    limit = PEOPLE_RESULT_LIMIT,
    preventErrorNotification = false,
  }: {
    document: TypedDocumentNode<
      ResultOf<typeof SEARCH_NOVA_SHAREABLE_USERS>,
      VariablesOf<typeof SEARCH_NOVA_SHAREABLE_USERS>
    >;
    enabled?: boolean;
    limit?: number;
    preventErrorNotification?: boolean;
  },
) {
  const [searchQuery, setSearchQuery] = useState('');
  useDebounce(() => setSearchQuery(query.trim()), 200, [query]);
  const { data, loading, error, refetch } = useQuery(document, {
    variables: { query: searchQuery, limit },
    skip: !enabled || !searchQuery,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    context: preventErrorNotification ? { notifications: { preventErrorNotification: true } } : undefined,
  });
  const isDebouncing = query.trim() !== searchQuery;
  const employees = useMemo<SettingsEmployee[]>(
    () =>
      !enabled || !searchQuery || isDebouncing
        ? []
        : (data?.employees ?? []).map((employee) => ({
            userUuid: employee.userUuid,
            email: employee.email,
            name: employee.fullName,
            imageUrl: employee.imageUrl ?? undefined,
            firstName: employee.firstName ?? undefined,
            lastName: employee.lastName ?? undefined,
            title: employee.title ?? undefined,
            status: 'active',
          })),
    [data?.employees, enabled, isDebouncing, searchQuery],
  );
  return {
    employees,
    loading: enabled && (loading || isDebouncing),
    error: enabled && !!searchQuery && !isDebouncing ? error : undefined,
    retry: () => void refetch().catch(() => undefined),
  };
}
